import * as React from "react";
import { ChevronsUpDown, Building2, Search, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTenant } from "@/contexts/TenantContext";
import { useTranslation } from "../hooks/useTranslation";
import { Iniziale } from "./sidebar-stile";

export function TeamSwitcher() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const {
    activeTenant,
    tenants,
    switchTenant,
    isMasterOrg,
    canCreateSubOrg,
    usageData,
  } = useTenant();

  // If no tenants available, show RAG Service branding
  if (!tenants || tenants.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Search className="size-4" />
            </div>
            {/*}
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">RAG Service</span>
              <span className="truncate text-xs">Knowledge Base</span>
            </div>
            */}
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!activeTenant) {
    return null;
  }

  const getOriginLabel = (origin) => {
    switch (origin) {
      case "sp":
        return "SSO Tenant";
      case "rag":
        return "RAG Tenant";
      case "rag+sp":
        return "Mixed Tenant";
      default:
        return "Tenant";
    }
  };

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="hover:bg-[#F1F5F9] data-[state=open]:bg-white data-[state=open]:shadow-[0_1px_2px_rgba(15,23,42,0.08),0_0_0_1px_#E2E8F0] [&>svg]:text-[#94A3B8]"
              >
                {/* L'organizzazione: la sua iniziale sul gradiente dell'onboarding. */}
                <Iniziale nome={activeTenant.name} grande />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-[#020617]">
                    {activeTenant.name}
                  </span>
                  <span className="truncate text-xs text-[#64748B]">
                    {t("nav.changeOrganization", "Change Organization")}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? "bottom" : "right"}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("nav.organizations", "Organizations")}
              </DropdownMenuLabel>
              {/* Le principali, ciascuna con le sue secondarie sotto; poi le
                  secondarie di cui non si vede la principale (es. chi e'
                  invitato solo nell'organizzazione di un cliente). */}
              {(() => {
                const idDi = (tenant) => tenant.tenant_id || tenant._id;
                // Per id (tenant_id o _id): due campi mancanti non devono sembrare uguali.
                const attiva = (tenant) => Boolean(activeTenant) && String(idDi(activeTenant)) === String(idDi(tenant));
                // Sotto il nome: il proprio ruolo dove non si e' titolari,
                // altrimenti se e' la principale o una secondaria.
                const descrizione = (tenant) =>
                  tenant.role && tenant.role !== "owner"
                    ? t(`components.members.${tenant.role}`, tenant.role)
                    : tenant.is_master === false || (tenant.is_master === undefined && tenant.parent_tenant_id)
                      ? t("nav.secondaryTenant", "Secondaria")
                      : t("nav.mainTenant", "Principale");
                const voce = (tenant, rientrata = false) => (
                  <DropdownMenuItem
                    key={idDi(tenant)}
                    onClick={() => switchTenant(tenant)}
                    className={`gap-2 p-2 ${rientrata ? "pl-5" : ""}`}
                    disabled={attiva(tenant)}
                  >
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-sm border">
                      <Building2 className="size-4 shrink-0" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm">{tenant.name}</span>
                      <span className="text-xs text-muted-foreground">{descrizione(tenant)}</span>
                    </div>
                    {attiva(tenant) && <DropdownMenuShortcut>✓</DropdownMenuShortcut>}
                  </DropdownMenuItem>
                );

                const principali = tenants.filter((tenant) => tenant.is_master !== false && !tenant.parent_tenant_id);
                const idPrincipali = new Set(principali.flatMap((tenant) => [tenant._id, tenant.tenant_id].filter(Boolean)));
                const secondarie = tenants.filter((tenant) => !principali.includes(tenant));
                const sottoA = (principale) =>
                  secondarie.filter(
                    (tenant) =>
                      tenant.parent_tenant_id &&
                      (tenant.parent_tenant_id === principale._id || tenant.parent_tenant_id === principale.tenant_id)
                  );
                const senzaPrincipale = secondarie.filter((tenant) => !idPrincipali.has(tenant.parent_tenant_id));

                return [
                  ...principali.map((principale) => (
                    <React.Fragment key={idDi(principale)}>
                      {voce(principale)}
                      {sottoA(principale).map((tenant) => voce(tenant, true))}
                    </React.Fragment>
                  )),
                  ...senzaPrincipale.map((tenant) => voce(tenant)),
                ];
              })()}
              {/* Tenant creation disabled - use membership invites instead */}
              {/*
              <DropdownMenuItem
                onClick={() => setIsCreateDialogOpen(true)}
                className="gap-2 p-2"
                disabled={!canCreateSubOrg}
              >
                <div className="flex size-6 items-center justify-center rounded-sm border border-dashed">
                  <Plus className="size-4 shrink-0" />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="font-medium">
                    {isMasterOrg
                      ? "Create Sub-Organization"
                      : "Create Organization"}
                  </span>
                  {!canCreateSubOrg && (
                    <span className="text-xs text-muted-foreground">
                      Switch to master org to create
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
              */}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
